package com.islamicmedia.shared.domain.model

import com.islamicmedia.shared.util.ReferralCodeFormat
import com.islamicmedia.shared.util.parseTimestampMillis
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * The member's own referral code and counts, exactly as GET /api/referral/me sends them:
 *
 *   {"status":"ok","code":"ABCD2345","display":"ABCD-2345","link":"https://…/r/ABCD2345","stats":{…},
 *    "can_redeem":true,"redeem_until":"2026-10-24T09:00:00Z","redeem_code":"ABCD-2345",
 *    "referral_blocked":false,"referral_blocked_at":null}
 *   {"status":"not_eligible","can_redeem":false,"redeem_until":null,"redeem_code":null,
 *    "referral_blocked":false,"referral_blocked_at":null}
 *       paused, past due, gifted, voucher or free: can't refer
 *   {"status":"disabled"}       the program is switched off server-side (REFERRALS_ENABLED)
 *
 * COUNTS ONLY, by design: the server never sends a friend's address, and nothing here should grow
 * one. The screen's words live in [com.islamicmedia.shared.presentation.ReferralCopy].
 */
@Serializable
data class ReferralInfo(
    val status: String = "error",
    /** Canonical, e.g. "ABCD2345". Only with status "ok". */
    val code: String? = null,
    /** For people, e.g. "ABCD-2345". */
    val display: String? = null,
    /** The share link: <PUBLIC_BASE_URL>/r/<code>, which redirects to /register?refcode=<code>. */
    val link: String? = null,
    val stats: ReferralStats? = null,
    /** True only when entering a friend's code (POST /api/referral/redeem, the "Got a code from a
     *  friend?" entry on this same screen) would currently be allowed for this member: a new
     *  subscriber, still in their first month, with no referral recorded yet. */
    @SerialName("can_redeem") val canRedeem: Boolean = false,
    /** When [canRedeem] stops being true (the end of the member's first month). Null unless
     *  [canRedeem]. */
    @SerialName("redeem_until") val redeemUntil: String? = null,
    /** A code this member attached before subscribing (the paywall's "Have a referral code?"),
     *  which redeem will use if they submit with the field left blank. Null otherwise. */
    @SerialName("redeem_code") val redeemCode: String? = null,
    /** True when THIS member's own purchase, as a REFEREE, was voided at payment (e.g. a card
     *  match — theirs or another referred member's) and the notice hasn't aged out server-side yet
     *  (owner's report, 2026-09-25: they were told nothing). See [ReferralCopy.REFERRAL_BLOCKED]
     *  and MediaCheckerViewModel's one-time notice. */
    @SerialName("referral_blocked") val referralBlocked: Boolean = false,
    /** When [referralBlocked] happened, ISO-8601. Null unless [referralBlocked]. */
    @SerialName("referral_blocked_at") val referralBlockedAt: String? = null,
) {
    val isOk: Boolean get() = status == "ok" && !code.isNullOrBlank()
    /** Not a member who can refer right now (see the KDoc above for who). */
    val isNotEligible: Boolean get() = status == "not_eligible"
    /** The whole program is off. Distinct from [isNotEligible]: telling a paying member to
     *  "subscribe to get your own code" would be wrong. */
    val isDisabled: Boolean get() = status == "disabled"

    /** "ABCD-2345", even from a server that didn't send `display`. */
    val displayCode: String? get() = display?.takeIf { it.isNotBlank() } ?: code?.let(ReferralCodeFormat::display)

    /** [redeemUntil] as "Oct 24, 2026", for the entry's "until ..." line. Null unless [canRedeem]. */
    fun redeemUntilDateLabel(zone: TimeZone = TimeZone.currentSystemDefault()): String? =
        redeemUntil?.takeIf { canRedeem }?.let { formatReferralDate(it, zone) }
}

/**
 * The /api/referral/me `stats` object (referral_stats in 2026-09-23_referrals.sql).
 *
 *  - [joined]: friends who subscribed with the code and kept it (a friend who cancelled, or was
 *    refunded, in their first month isn't counted: both free months were taken back).
 *  - [inHold]: friends still in their first month (a month from paying, even on a yearly plan),
 *    whose referral (and this member's free month for it) can still be taken back if they cancel.
 *  - [rewarded]: free months this member has been given and kept, all time.
 *  - [earned365]: the same, for friends who paid in the last 365 days. [cap] is the server's
 *    limit on those (effectively none since 2026-09-24; not shown in the app).
 *  - [nextReleaseAt]: always null now (a free month is given the moment the friend pays, so nothing
 *    waits to be released); kept so the contract keeps its shape, and the "unlocks on" line stays
 *    hidden.
 */
@Serializable
data class ReferralStats(
    val joined: Int = 0,
    @SerialName("in_hold") val inHold: Int = 0,
    val rewarded: Int = 0,
    @SerialName("earned_365") val earned365: Int = 0,
    val cap: Int = 12,
    @SerialName("next_release_at") val nextReleaseAt: String? = null,
) {
    /**
     * [nextReleaseAt] as a calendar date in the viewer's zone, e.g. "Oct 7, 2026", for
     * `ReferralCopy.nextRelease(...)`. Null when there's no date (always, today) or it won't parse.
     */
    fun nextReleaseDateLabel(zone: TimeZone = TimeZone.currentSystemDefault()): String? =
        formatReferralDate(nextReleaseAt, zone)
}

/**
 * POST /api/referral/attach's verdict. [status] is one of
 * ok | not_found | self | not_new | already_attached | referrer_ineligible | rate_limited | error,
 * where the last two are made up client-side (HTTP 429, and any failed call). [code] is the
 * canonical code, only with "ok".
 */
data class ReferralAttachResult(val status: String, val code: String? = null) {
    /**
     * Whether this answer settles the matter, so a code stored from a link can be forgotten.
     * rate_limited and error are worth another try on a later sign-in; everything else is final.
     */
    val isFinal: Boolean get() = status != "error" && status != "rate_limited"
}

/**
 * POST /api/referral/redeem's verdict — a code entered AFTER subscribing (the "Got a code from a
 * friend?" entry on the Free Gift screen, only offered while [ReferralInfo.canRedeem]). [status] is
 * one of ok | not_found | self | not_new | too_late | not_subscribed | already_referred |
 * referrer_ineligible | blocked | rate_limited | error, where the last two are made up client-side
 * (HTTP 429, and any failed call). "blocked" (a card match voided the referral at payment) reads as
 * [com.islamicmedia.shared.presentation.ReferralCopy.REFERRAL_BLOCKED].
 */
data class ReferralRedeemResult(
    val status: String,
    /** The friend's (this member's) new next-bill date, only with "ok"; null while the month is
     *  still being written in the background or has to be granted by hand — show
     *  [com.islamicmedia.shared.presentation.ReferralCopy.REDEEM_SUCCESS_NO_DATE] then. */
    val nextBill: String? = null,
    /** The code that counted (only with "ok"; may differ from what was typed when one was already
     *  attached — the first code attached wins). */
    val code: String? = null,
) {
    val isOk: Boolean get() = status == "ok"

    /** [nextBill] as "Nov 24, 2026", for `ReferralCopy.redeemSuccess(...)`. Null when there's no
     *  date to show — use [com.islamicmedia.shared.presentation.ReferralCopy.REDEEM_SUCCESS_NO_DATE]
     *  instead. */
    fun nextBillDateLabel(zone: TimeZone = TimeZone.currentSystemDefault()): String? =
        formatReferralDate(nextBill, zone)
}

/** "2026-11-24T09:00:00Z" -> "Nov 24, 2026" in [zone]. Null when [iso] is null or won't parse. Shared
 *  by [ReferralStats.nextReleaseDateLabel], [ReferralInfo.redeemUntilDateLabel] and
 *  [ReferralRedeemResult.nextBillDateLabel] so the three dates the referral screens show all read
 *  the same way — also public so a UI can format a bare ISO string it kept itself (e.g.
 *  MediaCheckerUiState.redeemSuccessNextBill, once the redeem dialog has closed). */
fun formatReferralDate(iso: String?, zone: TimeZone = TimeZone.currentSystemDefault()): String? {
    val ms = iso?.let { parseTimestampMillis(it) } ?: return null
    val date = Instant.fromEpochMilliseconds(ms).toLocalDateTime(zone).date
    val month = REFERRAL_SHORT_MONTHS.getOrNull(date.monthNumber - 1) ?: return null
    return "$month ${date.dayOfMonth}, ${date.year}"
}

private val REFERRAL_SHORT_MONTHS =
    listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
